import * as _ from 'lodash';
import { cloneDeep } from 'lodash';
import moment from 'moment';
import { CategoryModel } from './category.model';
import { NotificationIdentifierModel } from '../../low-level/notification-identifier.model';
import { CompositeModel } from '../../complex/composite.model';
import { ODataLTextModel } from '../../../utilities/text-input-utilities';
import { InternalInfoCategoryModel } from './internal-info-category.model';
import { InternalInfoContactModel } from './internal-info-contact.model';
import { DropdownModel } from '../dropdown.model';
import { DisplayDecorator } from '../../../decorators/display-decorator';
import { ConfigConstants } from '../../../config/config-constants';
import { assignModelOptions } from '../../../utilities/assign-model-options';
import { CustomInjector } from '../../../services/custom-injector/custom-injector';
import { ConfigService } from '../../../services/config/config.service';
import { InternalInfoListModel, ODataInternalInfoOutModel } from './internal-info-list.model';
import INTERNAL_INFO_CATEGORY = ConfigConstants.INTERNAL_INFO_CATEGORY;
import { assertUnreachable } from '../../../utilities/assert-unreachable';

export class InternalInfoModel extends CompositeModel {
    public notificationId: NotificationIdentifierModel;
    public functionalLoc: string;
    public headerInfo: string;

    public categoryModel: InternalInfoCategoryModel;
    public contactModel: DropdownModel<InternalInfoContactModel>;

    public originalContactModelList: Array<DisplayDecorator<InternalInfoContactModel>>;
    public originalCategoryModel: InternalInfoCategoryModel;

    private readonly gid: string;

    public accordionOpenedStatus: AccordionOpenedStatus = {
        contact: false,
        parking: false,
        technicalSpec: false,
        organisation: false,
        constancyCheck: false,
        dispatch: false,
        subSystem: false,
    };
    public constructor(
        categoryModel: InternalInfoCategoryModel,
        notificationId: NotificationIdentifierModel,
        contacts: InternalInfoContactModel[],
        functionalLoc: string,
    ) {
        super('IntInfDeep');
        const injector = CustomInjector.getInjector();
        const configService = injector.get(ConfigService);
        this.gid = configService.getUserFrontId();

        const internalContactList = contacts.map(
            (contactInfo) =>
                new DisplayDecorator(
                    `${contactInfo.functionName.textLine.value} ${contactInfo.contactName.textLine.value}`,
                    contactInfo,
                ),
        );

        this.contactModel = new DropdownModel('ContactDropDown', internalContactList);
        this.contactModel.selectByIndex(0);
        this.originalContactModelList = _.cloneDeep(internalContactList);

        this.notificationId = notificationId;
        this.functionalLoc = functionalLoc;

        this.categoryModel = categoryModel;
        this.originalCategoryModel = _.cloneDeep(categoryModel);

        this.getAndUpdateHeaderInfo();
    }

    public getAndUpdateHeaderInfo(): string {
        const gid = this.gid;
        const date = moment().format(ConfigConstants.formatPatternDateGeneric);
        return (this.headerInfo = `\n${date} by ${gid}`);
    }

    /**
     * Contacts dropdown display name is combination of function name and contact name
     * Every time when user Change/Add then the Function/contact name then display name should be updated
     */
    public updateDisplayName(): void {
        if (!this.contactModel.selectedEntry) {
            return;
        }
        const newContactInfoModel = new DisplayDecorator(
            `${this.contactModel.selectedEntry.model.functionName.textLine.value} ${this.contactModel.selectedEntry.model.contactName.textLine.value}`,
            this.contactModel.selectedEntry.model,
        );
        this.contactModel.selectedEntry.displayName = newContactInfoModel.displayName;
    }

    /**
     * As per requirement, after deleting contact
     * 1. User deletes the contact then next entry is selected
     * 2. If user delete last entry them first entry will be selected
     */
    public deleteInternalInfoContact(): void {
        const index = this.contactModel.value.findIndex(
            (contact) => contact.model === this.contactModel.selectedEntry.model,
        );
        this.contactModel.removeModel(index);

        if (this.contactModel.value.length < 1) {
            this.contactModel.deselect();
        } else if (this.contactModel.value.length >= index) {
            this.contactModel.selectByIndex(0);
        } else {
            this.contactModel.selectByIndex(index);
        }
    }

    /**
     * This function will restore original Values of contact if user discard without saving the data
     */
    public restoreOriginalContactsValues(): void {
        this.contactModel.value = _.cloneDeep(this.originalContactModelList);
        this.updateDisplayName();
        this.contactModel = assignModelOptions(
            this.contactModel,
            {
                ...this.contactModel.options,
                disabled: false,
            },
            this,
        );
        // dese lect dropdown if original list is empty else select first dropdown
        if (this.originalContactModelList.length < 1) {
            this.contactModel.deselect();
            return;
        }
        this.contactModel.selectByIndex(0);
    }

    /**
     * As per the requirement, When user switch from one category to other category
     * If user entered Invalid entry in any field and contact is already saved then restore the Invalid contact with original value
     * If user added new contact and if new contact is Invalid, then deleted the new contact from list
     */
    public deleteInvalidContactsFromList(): void {
        this.contactModel.value.map((contact, key) => {
            if (contact.model.formValidationStatus === ConfigConstants.FORM_STATUS.INVALID) {
                const originalContactValue = this.originalContactModelList.find(
                    (newContact) => newContact.model.uniqueId === contact.model.uniqueId,
                );
                this.contactModel.deselect();
                if (originalContactValue) {
                    this.contactModel.value[key] = cloneDeep(originalContactValue);
                    return;
                }
                this.contactModel.value.splice(key);
            }
        });
        this.updateDisplayName();
    }

    /**
     * Restore original category values
     */
    public restoreOriginalCategoryValues(): void {
        this.categoryModel = _.cloneDeep(this.originalCategoryModel);
    }

    /**
     * Restore original category values for a given category
     *
     * @param selectedCategory
     */
    public restoreOriginalCategoryValuesForSelectedCategory(selectedCategory: INTERNAL_INFO_CATEGORY): void {
        switch (selectedCategory) {
            case INTERNAL_INFO_CATEGORY.PARKING:
                this.categoryModel.parking = _.cloneDeep(this.originalCategoryModel.parking);
                break;
            case INTERNAL_INFO_CATEGORY.ORG_MATTERS:
                this.categoryModel.organisation = _.cloneDeep(this.originalCategoryModel.organisation);
                break;
            case INTERNAL_INFO_CATEGORY.TECHNICAL_SPEC:
                this.categoryModel.technicalSpec = _.cloneDeep(this.originalCategoryModel.technicalSpec);
                break;
            case INTERNAL_INFO_CATEGORY.CONSTANCY_CHECK:
                this.categoryModel.constancyCheck = _.cloneDeep(this.originalCategoryModel.constancyCheck);
                break;
            case INTERNAL_INFO_CATEGORY.DISPATCH:
                this.categoryModel.dispatch = _.cloneDeep(this.originalCategoryModel.dispatch);
                break;
            case INTERNAL_INFO_CATEGORY.SUB_SYSTEMS:
                this.categoryModel.subSystem = _.cloneDeep(this.originalCategoryModel.subSystem);
                break;
            case INTERNAL_INFO_CATEGORY.CONTACT:
                // Contacts are not being manually restored, but have their own mechanism restoreOriginalContactsValues().
                break;
            default:
                assertUnreachable(selectedCategory);
        }
    }

    public getCategoryValues(selectedCategory: INTERNAL_INFO_CATEGORY): CategoryModel {
        return this.categoryModel.getSelectedCategory(selectedCategory);
    }

    /**
     * There are 6 categories
     * We have to check if there is any changes done in those category  by comparing text
     * if there is any change,then we have to update header info i.e "Date by GID" to know who and when the data is changed
     */
    public updateHeaderInfoIfValueIsChanged(): void {
        this.originalCategoryModel.categoryArray.forEach((originalCategoryValues: CategoryModel, key1: number) => {
            this.categoryModel.getAllCategoriesModel().forEach((newCategoryValues: CategoryModel, key2: number) => {
                if (
                    key1 === key2 &&
                    originalCategoryValues.textId === newCategoryValues.textId &&
                    originalCategoryValues.textLine.value !== newCategoryValues.textLine.value
                ) {
                    this.categoryModel.updateHeaderInfo(newCategoryValues);
                }
            });
        });
    }

    /**
     * Iterating original values and current value to check if any value is changed in any of category model
     */
    public isCategoryValuesChanged(): boolean {
        const changedObjects = this.originalCategoryModel.categoryArray.filter((original) => {
            const currentModel = this.categoryModel
                .getAllCategoriesModel()
                .find((current) => current.textId === original.textId);

            if (currentModel) {
                return original.textLine.value !== currentModel.textLine.value;
            }
        });
        return changedObjects.length > 0;
    }

    /**
     * Iterating original values and current value to check if any value is changed in any of contact model
     */
    public isContactValuesChanged(): boolean {
        // If c ontact length doesn't match with original contact , it means either added or deleted
        if (this.originalContactModelList.length !== this.contactModel.value.length) {
            return true;
        }
        return this.originalContactModelList.some((original) => {
            const currentContact = this.contactModel.value.find(
                (current) => current.model.uniqueId === original.model.uniqueId,
            );
            if (currentContact) {
                return !_.isEqual(original.model.contactDetails, currentContact.model.contactDetails);
            }
            return true;
        });
    }

    /**
     * Creating the new contact on Add button click
     */
    public createContactModel(): void {
        const newContactInfo = new InternalInfoContactModel('', '', '', '', '', '', '', this.functionalLoc);

        const newContactInfoModel = new DisplayDecorator(``, newContactInfo);

        this.contactModel.addModel(newContactInfoModel);
        this.contactModel.selectByDisplayName(newContactInfo.functionName.textLine.value);
    }

    /**
     * Each category have its own TextId,
     * Since we get all the text id in single array from backend,
     * so we have to filter the result by textId and bind to its respective category
     *
     * @param response from InternalInfoLongText which we get from NDP
     * @param notificationID
     * @param functionalLoc
     */
    public static buildInternalInfoModel(
        response: ODataLTextModel[],
        notificationID: NotificationIdentifierModel,
        functionalLoc: string,
    ): InternalInfoModel {
        // Build category model and contact Model
        const internalInfoCategoryModel = InternalInfoCategoryModel.buildCategoryModel(response, functionalLoc);
        const internalInfoContactList = InternalInfoContactModel.buildContactModel(response, functionalLoc);

        return new InternalInfoModel(internalInfoCategoryModel, notificationID, internalInfoContactList, functionalLoc);
    }

    public getOutModel(): ODataInternalInfoOutModel {
        const contactModelList = this.contactModel.value
            .filter((contactModel) => contactModel.model.formValidationStatus !== ConfigConstants.FORM_STATUS.INVALID)
            .map((contact) => contact.model);

        // if c ontact screen is empty then we have to send empty contact model
        if (contactModelList.length < 1) {
            const newContactInfo = new InternalInfoContactModel('', '', '', '', '', '', '', this.functionalLoc);
            contactModelList.push(newContactInfo);
        }

        const contactListModel = new InternalInfoListModel('Contacts', contactModelList);

        const outModel = <ODataInternalInfoOutModel>contactListModel.getOutModel();

        outModel[this.name] = outModel.Contacts;

        delete (outModel as Omit<ODataInternalInfoOutModel, 'Contacts'>).Contacts;

        // curr ent outModel structure [zc01,zc02,zc03,zc04,zc05,zc06,zc07, Zc01,zc02,zc03,zc04,zc05,zc06,zc07]
        // belo w code sort the way backend expect i.e. [zc01,zc01,zC02,zC02,zc03,zc03,zc04,zc04,zc05, zc05,zc06,zc06,zc07,zc07]
        outModel[this.name].sort((a, b) => a.TextID.localeCompare(b.TextID, 'en', { numeric: true }));

        return outModel;
    }
}

interface AccordionOpenedStatus {
    [key: string]: boolean; // Index signature
    contact: boolean;
    parking: boolean;
    technicalSpec: boolean;
    organisation: boolean;
    constancyCheck: boolean;
    dispatch: boolean;
    subSystem: boolean;
}
